import { ChatProvider, useChatStore } from "@eventr/common-storybook";
import { ReactNode, useEffect, useMemo } from "react";
import { useBoundStore } from "state/bound.store";
import { useLanguageContext } from "state/languageState/languageContext";
import { useLiveToken } from "state/rocketchatState/rocketchatUi.store";
import { appChatConfig } from "./chat-config";

/**
 * Connects the chat component library for the selected organisation's chat. Renders its
 * children bare until a chat token is live; `useAppChatReady` tells screens which.
 */
export const AppChatProvider = ({ children }: { children: ReactNode }) => {
  const token = useLiveToken();
  const locale = useLanguageContext((s) => s.getLanguageISO639Code());
  const timeFormat = useLanguageContext((s) => s.timeFormat);
  const users = useBoundStore((s) => s.users);

  const config = useMemo(
    () => (token ? appChatConfig(token, { locale, timeFormat, users }) : undefined),
    // Keyed on the credentials, not the token object, which is replaced on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token?.org, token?.userId, token?.authToken, locale, timeFormat, users],
  );

  useEffect(() => {
    if (!token) useChatStore.getState().reset();
  }, [token]);

  if (!config) return <>{children}</>;
  return <ChatProvider config={config}>{children}</ChatProvider>;
};

/** Whether `AppChatProvider` is connected, so chat library components can render. */
export const useAppChatReady = () => Boolean(useLiveToken());
